'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Calendar,
  Users,
  Zap,
  Inbox,
  Settings,
  Menu,
  X,
  LogOut,
  Building2,
  Package,
  Activity,
  FlaskConical,
} from 'lucide-react';
import { DirhamIcon } from '@/components/shared/icons/dirham-icon';
import { useAuth } from '@/lib/integrations/supabase';
import { getVisibleNavItems } from '@/lib/core/auth';
import { useBusiness, prefetchOnHover } from '@/lib/features/swr';
import { useNotifications } from '@/lib/features/notifications';
import { useLanguage } from '@/lib/integrations/language';
import { AnimatedThemeToggler } from '@/components/shared/ui/animated-theme-toggler';
import { AdminContextReset } from '@/components/features/admin';

interface NavItem {
  href: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  labelKey: string; // Translation key
  badge?: number;
}

// Navigation items with translation keys
const allNavItems: NavItem[] = [
  { href: '/home', icon: Home, labelKey: 'home' },
  { href: '/schedule', icon: Calendar, labelKey: 'bookings' },
  { href: '/sales', icon: DirhamIcon, labelKey: 'sales' },
  { href: '/customers', icon: Users, labelKey: 'customers' },
  { href: '/inventory', icon: Package, labelKey: 'inventory' },
  { href: '/automations', icon: Zap, labelKey: 'automations' },
  { href: '/inbox', icon: Inbox, labelKey: 'inbox' },
  { href: '/settings', icon: Settings, labelKey: 'settings' },
  { href: '/admin/monitoring', icon: Activity, labelKey: 'monitoring' },
  { href: '/testing', icon: FlaskConical, labelKey: 'testing' },
];

export function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation('navigation');
  const { isRTL } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClosingMobileMenu, setIsClosingMobileMenu] = useState(false);
  const [isOpeningMobileMenu, setIsOpeningMobileMenu] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // Always start collapsed
  const [isHovered, setIsHovered] = useState(false);
  const [expandedWidth, setExpandedWidth] = useState(256); // Default width, will be measured
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const { profile, role, businessId, signOut, isLoading, isAdmin, adminSelectedBusinessId, effectiveBusinessId } = useAuth();
  const [isPWA, setIsPWA] = useState(false);

  // Detect if running in PWA (standalone mode)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if in standalone mode (PWA)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          (window.navigator as any).standalone === true ||
                          document.referrer.includes('android-app://');
      setIsPWA(isStandalone);
    }
  }, []);

  // Fetch unread notification count for badge (using notification context for real-time updates)
  const { unreadCount } = useNotifications();

  // Load business data using SWR hook (cached, deduplicated, auto-revalidated)
  const activeBusinessId = effectiveBusinessId || businessId;
  const { data: business, mutate: refreshBusiness } = useBusiness(activeBusinessId);

  // Sidebar always starts collapsed by default (hover-only expansion)
  // No localStorage persistence - always collapsed on page load

  // Handle hover for desktop sidebar expansion
  const handleMouseEnter = () => {
    if (isCollapsed) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setIsHovered(false);
    }
  };

  // Determine if sidebar should appear expanded (for desktop hover effect)
  const isExpanded = isHovered && isCollapsed;

  // Handle logout
  const handleLogout = async () => {
    await signOut();
    router.push('/login');
  };

  // Filter nav items based on user role and add dynamic badges
  const visiblePaths = getVisibleNavItems(role);
  const visibleNavItems = allNavItems
    .filter(item => visiblePaths.includes(item.href))
    .map(item => {
      // Add unread count badge to Inbox
      if (item.href === '/inbox' && unreadCount > 0) {
        return { ...item, badge: unreadCount };
      }
      return item;
    });

  // Get user initials for avatar
  const getUserInitials = () => {
    if (profile?.full_name) {
      const parts = profile.full_name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return profile.full_name.substring(0, 2).toUpperCase();
    }
    if (profile?.email) {
      return profile.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Get display role (translated)
  const getDisplayRole = () => {
    if (!role) return t('roles.guest');
    return t(`roles.${role}`, { defaultValue: t('roles.staff') });
  };

  // Get display name
  const getDisplayName = () => {
    if (profile?.full_name) return profile.full_name;
    if (profile?.email) return profile.email.split('@')[0];
    return 'User';
  };

  // Get business name
  const businessName = business?.name || 'evrls';

  // Calculate expanded width based on content - use a simple heuristic instead of DOM measurement
  // This avoids expensive DOM operations on every render
  useEffect(() => {
    // Simple heuristic: estimate width based on character count
    // Average character width is ~8px for 14px font, ~10px for 16px font
    const businessNameLength = (businessName || 'evrls').length;
    const userNameLength = getDisplayName().length;

    // Find longest nav item label
    let maxNavLength = 0;
    visibleNavItems.forEach(item => {
      const label = t(item.labelKey);
      if (label.length > maxNavLength) {
        maxNavLength = label.length;
      }
    });

    // Estimate widths (character count * avg char width + padding)
    const businessNameWidth = businessNameLength * 10 + 32; // 16px font
    const navWidth = maxNavLength * 8 + 80; // 14px font + icon + badge space
    const userNameWidth = userNameLength * 8 + 60; // 14px font + avatar

    const longestWidth = Math.max(businessNameWidth, navWidth, userNameWidth);
    const calculatedWidth = Math.max(280, Math.min(longestWidth + 40, 500));

    setExpandedWidth(calculatedWidth);
  }, [businessName, visibleNavItems, profile, t]);

  // Subscribe to business updates (from settings page)
  useEffect(() => {
    const handleBusinessUpdate = () => {
      refreshBusiness();
    };
    window.addEventListener('business-updated', handleBusinessUpdate);
    return () => window.removeEventListener('business-updated', handleBusinessUpdate);
  }, [refreshBusiness]);

  // Set CSS variable for sidebar open state (for assistant dock positioning)
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.documentElement.style.setProperty('--sidebar-open', '1');
    } else {
      document.documentElement.style.setProperty('--sidebar-open', '0');
    }
    return () => {
      document.documentElement.style.setProperty('--sidebar-open', '0');
    };
  }, [isMobileMenuOpen]);

  // Update theme color dynamically for iOS Safari chrome when menu opens/closes
  useEffect(() => {
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColorMeta);
    }
    
    // When menu is open, set theme color to match sidebar
    // When closed, revert to app's default background color
    const isDark = document.documentElement.classList.contains('dark');
    if (isMobileMenuOpen && !isClosingMobileMenu) {
      themeColorMeta.setAttribute('content', isDark ? '#252525' : '#FFFFFF');
    } else if (!isMobileMenuOpen && !isClosingMobileMenu) {
      // Revert to app's default background color when menu is closed
      themeColorMeta.setAttribute('content', isDark ? '#1A1A1A' : '#FAFAFA');
    }
  }, [isMobileMenuOpen, isClosingMobileMenu]);

  // Handle closing mobile menu with animation
  const closeMobileMenu = useCallback(() => {
    // Reset opening state if it was opening
    setIsOpeningMobileMenu(false);
    // Start closing animation (slide out to left)
    setIsClosingMobileMenu(true);
    // Wait for animation to complete (300ms duration) before removing from DOM
    setTimeout(() => {
      setIsClosingMobileMenu(false);
      setIsMobileMenuOpen(false);
    }, 300);
  }, []);

  const toggleMobileMenu = () => {
    if (!isMobileMenuOpen) {
      // Opening: set open state first, then trigger animation
      setIsMobileMenuOpen(true);
      // Use requestAnimationFrame to ensure DOM update before animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsOpeningMobileMenu(true);
        });
      });
    } else {
      // Closing: use the close function which handles animation
      closeMobileMenu();
    }
  };

  const sidebarContent = (
    <>
      {/* Business Branding */}
      <motion.div
        className="py-5 border-b border-border flex-shrink-0"
        initial={false}
        animate={{
          paddingLeft: (isCollapsed && !isExpanded) ? '0.5rem' : '1rem',
          paddingRight: (isCollapsed && !isExpanded) ? '0.5rem' : '1rem',
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
        }}
      >
        <Link
          href="/settings"
          className="flex items-center gap-3 group cursor-pointer"
          title={isCollapsed && !isExpanded ? businessName : undefined}
        >
          {/* Business Logo */}
          <div className="w-14 h-14 rounded-xl bg-[#FFF4ED] dark:bg-[rgba(255,107,53,0.15)] flex items-center justify-center flex-shrink-0 overflow-hidden border border-border lg:group-hover:border-primary/30 transition-smooth">
            {business?.logo_url ? (
              <Image
                src={business.logo_url}
                alt={businessName}
                width={56}
                height={56}
                className="w-full h-full object-cover"
              />
            ) : (
              <Building2 className="w-7 h-7 text-primary" />
            )}
          </div>
          {/* Business Name - Desktop: Animated, Mobile: Always visible */}
          <motion.div
            className="flex-1 min-w-0 overflow-hidden hidden lg:block"
            initial={false}
            animate={{
              opacity: (!isCollapsed || isExpanded) ? 1 : 0,
              width: (!isCollapsed || isExpanded) ? 'auto' : 0,
            }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            <h1 className="text-base font-semibold text-foreground group-hover:text-primary transition-smooth whitespace-nowrap">
              {businessName}
            </h1>
          </motion.div>
          {/* Mobile: Always show business name */}
          <div className="flex-1 min-w-0 lg:hidden">
            <h1 className="text-base font-semibold text-foreground truncate transition-smooth">
              {businessName}
            </h1>
          </div>
        </Link>
        {/* Admin Context Reset Button - shows when admin has selected a business */}
        {isAdmin && adminSelectedBusinessId && (
          <>
            {/* Desktop: Animated visibility based on sidebar state */}
            <motion.div
              className="mt-3 hidden lg:block"
              initial={false}
              animate={{
                opacity: (!isCollapsed || isExpanded) ? 1 : 0,
                height: (!isCollapsed || isExpanded) ? 'auto' : 0,
              }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <AdminContextReset variant="button" className="w-full justify-center text-xs" />
            </motion.div>
            {/* Mobile: Always visible */}
            <div className="mt-3 lg:hidden">
              <AdminContextReset variant="button" className="w-full justify-center text-xs" />
            </div>
          </>
        )}
      </motion.div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto min-h-0">
        {isLoading ? (
          // Show skeleton loading state while auth is loading
          <div className="space-y-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`flex items-center gap-3 px-4 py-3 rounded-full animate-pulse ${isCollapsed ? 'justify-center' : ''}`}
              >
                <div className="w-5 h-5 rounded bg-muted" />
                {!isCollapsed && <div className="h-4 w-20 rounded bg-muted" />}
              </div>
            ))}
          </div>
        ) : visibleNavItems.length === 0 ? (
          // Show message when no nav items (shouldn't happen normally)
          <div className={`px-4 py-3 text-sm text-muted-foreground ${isCollapsed ? 'text-center' : ''}`}>
            {!isCollapsed && 'No pages available'}
          </div>
        ) : (
          visibleNavItems.map((item) => {
            const isActive = pathname === item.href;
            const Icon = item.icon;

            // Prefetch page route AND data on hover for faster navigation
            const handleNavMouseEnter = () => {
              // Prefetch the Next.js route
              router.prefetch(item.href);

              // Prefetch the actual data based on the target page
              const targetMap: Record<string, 'home' | 'schedule' | 'customers' | 'sales' | 'inventory' | 'settings'> = {
                '/home': 'home',
                '/schedule': 'schedule',
                '/customers': 'customers',
                '/sales': 'sales',
                '/inventory': 'inventory',
                '/settings': 'settings',
              };
              const target = targetMap[item.href];
              if (target) {
                prefetchOnHover(activeBusinessId, target);
              }
            };

            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                onMouseEnter={handleNavMouseEnter}
                title={isCollapsed ? t(item.labelKey) : undefined}
                className={`
                  flex items-center justify-between gap-3 px-4 py-3 rounded-full text-sm font-medium transition-smooth relative group cursor-pointer
                  ${
                    isActive
                      ? 'bg-[#FFF4ED] dark:bg-[rgba(255,107,53,0.15)] text-primary border border-border'
                      : 'text-muted-foreground lg:hover:bg-muted lg:hover:text-foreground'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={2} />
                  {/* Desktop: Animated text */}
                  <motion.span
                    initial={false}
                    animate={{
                      opacity: (!isCollapsed || isExpanded) ? 1 : 0,
                      width: (!isCollapsed || isExpanded) ? 'auto' : 0,
                    }}
                    transition={{
                      duration: 0.3,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    className="overflow-hidden whitespace-nowrap hidden lg:inline"
                  >
                    {t(item.labelKey)}
                  </motion.span>
                  {/* Mobile: Always show text */}
                  <span className="lg:hidden whitespace-nowrap">
                    {t(item.labelKey)}
                  </span>
                </div>
                {item.badge && (
                  <>
                    {/* Desktop: Animated badge */}
                    <motion.span
                      initial={false}
                      animate={{
                        opacity: (!isCollapsed || isExpanded) ? 1 : 0,
                        width: (!isCollapsed || isExpanded) ? 'auto' : 0,
                        scale: (!isCollapsed || isExpanded) ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: [0.4, 0, 0.2, 1],
                      }}
                      className="hidden lg:flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold overflow-hidden"
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </motion.span>
                    {/* Mobile: Always show badge */}
                    <span className="lg:hidden flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                    {/* Desktop collapsed state: Small absolute badge - hidden on mobile */}
                    {isCollapsed && !isExpanded && (
                      <span className="hidden lg:flex absolute -top-1 -right-1 items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </>
                )}
                {/* Tooltip for collapsed state - Desktop only */}
                {isCollapsed && !isExpanded && (
                  <span className={`hidden lg:block absolute ${isRTL ? 'right-full mr-2' : 'left-full ml-2'} px-3 py-1.5 bg-foreground text-background text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50`}>
                    {t(item.labelKey)}
                  </span>
                )}
              </Link>
            );
          })
        )}
      </nav>

      {/* User profile */}
      <motion.div
        className="py-5 border-t border-border flex-shrink-0"
        initial={false}
        animate={{
          paddingLeft: (isCollapsed && !isExpanded) ? '0.75rem' : '1.5rem',
          paddingRight: (isCollapsed && !isExpanded) ? '0.75rem' : '1.5rem',
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-medium text-sm flex-shrink-0 overflow-hidden">
              {profile?.avatar_url ? (
                <Image
                  src={profile.avatar_url}
                  alt={getDisplayName()}
                  width={40}
                  height={40}
                  className="w-full h-full object-cover"
                />
              ) : (
                getUserInitials()
              )}
            </div>
            {/* Desktop: Animated user info */}
            <motion.div
              className="flex-1 min-w-0 overflow-hidden hidden lg:block"
              initial={false}
              animate={{
                opacity: (!isCollapsed || isExpanded) ? 1 : 0,
                width: (!isCollapsed || isExpanded) ? 'auto' : 0,
              }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <div className="text-sm font-medium text-foreground whitespace-nowrap">
                {getDisplayName()}
              </div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{getDisplayRole()}</div>
            </motion.div>
            {/* Mobile: Always show user info */}
            <div className="flex-1 min-w-0 lg:hidden">
              <div className="text-sm font-medium text-foreground truncate">
                {getDisplayName()}
              </div>
              <div className="text-xs text-muted-foreground">{getDisplayRole()}</div>
            </div>
            {/* Action buttons - Desktop: only show when expanded or hovered, Mobile: always show */}
            <motion.div
              className="hidden lg:flex items-center gap-1 flex-shrink-0"
              initial={false}
              animate={{
                opacity: (!isCollapsed || isExpanded) ? 1 : 0,
                width: (!isCollapsed || isExpanded) ? 'auto' : 0,
                scale: (!isCollapsed || isExpanded) ? 1 : 0,
              }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
              style={{ overflow: 'hidden' }}
            >
              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="p-1.5 hover:bg-muted rounded-lg transition-smooth flex-shrink-0 cursor-pointer"
                title="Logout"
              >
                <LogOut className="w-4 h-4 text-muted-foreground" />
              </button>
            </motion.div>
            {/* Mobile: Always show action buttons */}
            <div className="flex items-center gap-1 flex-shrink-0 lg:hidden">
              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-lg transition-smooth flex-shrink-0 cursor-pointer"
                title="Logout"
              >
                <LogOut className="w-4 h-4 text-muted-foreground" />
              </button>
              {/* Collapse toggle button - hidden on mobile */}
            </div>
          </div>
        )}
      </motion.div>
    </>
  );

  return (
    <>
      {/* Mobile header - Hamburger menu (fixed position, swaps sides based on language) */}
      <button
        onClick={toggleMobileMenu}
        className={`lg:hidden fixed z-[70] p-1.5 bg-card border border-border rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.001),0_3px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_3px_8px_rgba(0,0,0,0.2)] cursor-pointer transition-transform active:scale-95 ${
          isRTL ? 'right-4' : 'left-4'
        }`}
        style={{
          top: isPWA ? 'calc(env(safe-area-inset-top) + 0.5rem)' : '0.5rem'
        }}
      >
        {isMobileMenuOpen || isClosingMobileMenu ? (
          <X className="w-5 h-5 text-foreground" />
        ) : (
          <Menu className="w-5 h-5 text-foreground" />
        )}
      </button>

      {/* Mobile header - Theme toggler (fixed position, swaps sides based on language) */}
      <div
        className={`lg:hidden fixed z-[70] bg-card border border-border rounded-full px-2 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.001),0_3px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_3px_8px_rgba(0,0,0,0.2)] ${
          isRTL ? 'left-4' : 'right-4'
        }`}
        style={{
          top: isPWA ? 'calc(env(safe-area-inset-top) + 0.5rem)' : '0.5rem'
        }}
      >
        <AnimatedThemeToggler className="p-0.5 rounded-full hover:bg-muted transition-colors" />
      </div>

      {/* Desktop sidebar - Unified canvas design with dynamic corner radius (RTL-aware) */}
      {/* z-[100] when expanded/hovered to be above chatbot (z-[86]), z-[80] when collapsed */}
      <motion.aside
        data-sidebar="desktop"
        className={`hidden lg:flex fixed flex-col overflow-hidden bg-card ${isExpanded ? 'z-[100]' : 'z-[80]'}`}
        style={{
          [isRTL ? 'right' : 'left']: '1rem', // 16px from edge (left in LTR, right in RTL)
          top: '1rem', // 16px from top
          bottom: '1rem', // 16px from bottom
        }}
        initial={false}
        animate={{
          width: isExpanded ? expandedWidth : isCollapsed ? 80 : 256,
          // Dynamic corner radius - expands smoothly with width
          borderTopLeftRadius: '24px',
          borderBottomLeftRadius: '24px',
          borderTopRightRadius: isExpanded ? '24px' : '24px',
          borderBottomRightRadius: isExpanded ? '24px' : '24px',
          // Soft shadow that intensifies slightly on hover/expand
          boxShadow: isExpanded
            ? '0 6px 28px -2px rgba(0, 0, 0, 0.1), 0 3px 10px -1px rgba(0, 0, 0, 0.06)'
            : '0 4px 24px -2px rgba(0, 0, 0, 0.08), 0 2px 8px -1px rgba(0, 0, 0, 0.04)',
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1], // Smooth ease-in-out curve
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Subtle border overlay for depth - animated border radius */}
        <motion.div
          className="absolute inset-0 border border-border/50 pointer-events-none"
          animate={{
            borderTopLeftRadius: '24px',
            borderBottomLeftRadius: '24px',
            borderTopRightRadius: isExpanded ? '24px' : '24px',
            borderBottomRightRadius: isExpanded ? '24px' : '24px',
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          }}
        />

        <div ref={sidebarContentRef} className="flex flex-col h-full relative z-10">
          {sidebarContent}
        </div>
      </motion.aside>

      {/* Mobile sidebar */}
      {(isMobileMenuOpen || isClosingMobileMenu) && (
        <>
          {/* Full-screen backdrop - covers entire viewport including iOS safe areas */}
          {/* z-[90] ensures it's above chatbot (z-[86]) and overlay (z-[85]) */}
          <div
            className={`lg:hidden fixed bg-background z-[90] transition-opacity duration-300 ${
              isClosingMobileMenu ? 'opacity-0' : 'opacity-100'
            }`}
            style={{
              top: `calc(-1 * env(safe-area-inset-top, 0px))`,
              bottom: `calc(-1 * env(safe-area-inset-bottom, 0px))`,
              left: `calc(-1 * env(safe-area-inset-left, 0px))`,
              right: `calc(-1 * env(safe-area-inset-right, 0px))`,
            }}
            onClick={closeMobileMenu}
          />

          {/* Sidebar - Slide Animation (RTL-aware: slides from right in Arabic) */}
          {/* z-[91] ensures it's above backdrop (z-[90]) and chatbot (z-[86]) */}
          <aside
            className={`lg:hidden fixed ${isRTL ? 'right-0 border-l' : 'left-0 border-r'} w-60 max-w-[75vw] border-border bg-sidebar z-[91] flex flex-col transition-transform duration-300 ease-in-out ${
              isClosingMobileMenu
                ? (isRTL ? 'translate-x-full' : '-translate-x-full')
                : isOpeningMobileMenu
                ? 'translate-x-0'
                : (isRTL ? 'translate-x-full' : '-translate-x-full')
            }`}
            style={{
              top: 0,
              bottom: 0,
              height: '100dvh',
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Content wrapper - scrollable */}
            <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
              {sidebarContent}
            </div>
          </aside>
        </>
      )}
    </>
  );
}
